/**
 * Governance Config Store — Persisted Policy Overrides
 *
 * RESOLVES: CONFLICT-03 (Governance Policy Settings Not Persisted)
 *
 * Stores governance policy overrides via dataLayer so that
 * GovernanceSettingsTab customizations survive page reloads.
 *
 * The policyEngine reads from this store at initialization,
 * merging overrides with defaultPolicy.
 *
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 */

import { debug, warn } from '../../utils/logger';
import { dataLayer } from '../../data/dataLayer';
import { STAGES } from './stageDefinitions';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Staged enforcement mode for a governance scope (FG4 §F0.C).
 * - 'off'     — rule not evaluated.
 * - 'shadow'  — rule evaluated, findings emitted to shadowFindingsStore, NEVER blocks.
 * - 'warn'    — rule evaluated, surfaced as a non-blocking warning.
 * - 'enforce' — rule evaluated, blocks on failure.
 *
 * Anti-bandaid principle #4: every scope ramps off → shadow → warn → enforce.
 */
export type EnforcementMode = 'off' | 'shadow' | 'warn' | 'enforce';

/**
 * Grace ledger entry (FG4 §F0.C). A NAMED, COUNTABLE, OWNED exception that
 * temporarily degrades severity for a scope until `until`. Replaces implicit
 * `return OK` permissiveness — permissiveness becomes a documented debt with
 * an expiry date (anti-bandaid principle #3).
 */
export interface GraceLedgerEntry {
  /** Scope this grace applies to. At least one field should be set. */
  scope: {
    module?: string;
    family?: string;
    questionId?: string;
  };
  /** ISO date string. After this date the grace no longer applies. */
  until: string;
  /** Why this grace exists. */
  reason: string;
  /** Who owns closing this debt. */
  owner: string;
  /** Severity to degrade matching findings to while grace is active (default 50). */
  degradeToSeverity?: number;
}
export interface GovernanceConfigOverrides {
  /** Schema version for migration safety */
  version: 1;

  /** Quality gate overrides */
  qualityGates?: {
    minCogLevel?: number;
    maxCogLevel?: number;
    minDidacticDepth?: number;
    minQualityScore?: number;
  };

  /** Pipeline stage toggles */
  pipelineStages?: {
    schemaEnabled?: boolean;
    typeEnabled?: boolean;
    contentEnabled?: boolean;
    supportEnabled?: boolean;
    visualEnabled?: boolean;
    graphEnabled?: boolean;
    pedagogyEnabled?: boolean;
  };

  /** Pipeline behavior */
  pipelineBehavior?: {
    strictMode?: boolean;
    autoBlock?: boolean;
  };

  /** Policy overrides */
  policy?: {
    minQuestionsPerSkill?: number;
    maxQuestionsPerSkill?: number;
    requireHints?: boolean;
    requireExplanation?: boolean;
    requireVisuals?: boolean;
    distributionCheck?: boolean;
  };

  /**
   * Staged enforcement (FG4 §F0.C). The single source of truth for pedagogy
   * strictness, per module / per question-type. (C3 hardening-audit: this
   * replaced — and fully superseded — the removed global
   * `enforcePedagogicalContract: boolean` flag.) When a scope is omitted the
   * resolver falls back to `'enforce'` (see resolvePedagogicalMode).
   */
  enforcement?: {
    /** Per-module pedagogical-contract enforcement mode (key = module code). */
    pedagogical?: Record<string, EnforcementMode>;
    /** Per-question-type strict-schema enforcement mode (key = QuestionType). */
    schemas?: Record<string, EnforcementMode>;
  };

  /** Grace ledger — named, owned, expiring exceptions (FG4 §F0.C). */
  graceLedger?: GraceLedgerEntry[];

  /** Notification preferences */
  notifications?: {
    notifyBlockers?: boolean;
    notifyWarnings?: boolean;
    notifyWeeklyReport?: boolean;
    notifyNewRules?: boolean;
  };

  /** System settings */
  system?: {
    cacheEnabled?: boolean;
    debugMode?: boolean;
    autoValidateOnSave?: boolean;
    batchSize?: number;
  };

  /** Timestamp of last save */
  lastSaved?: number;
}

// ─── Default Config ───────────────────────────────────────────────────────────

const DEFAULT_CONFIG: GovernanceConfigOverrides = {
  version: 1,
  qualityGates: {
    minCogLevel: 2,
    maxCogLevel: 5,
    minDidacticDepth: 2,
    minQualityScore: 70
  },
  pipelineStages: {
    schemaEnabled: true,
    typeEnabled: true,
    contentEnabled: true,
    supportEnabled: true,
    visualEnabled: true,
    graphEnabled: true,
    pedagogyEnabled: true
  },
  pipelineBehavior: {
    strictMode: false,
    autoBlock: true
  },
  policy: {
    minQuestionsPerSkill: 5,
    maxQuestionsPerSkill: 50,
    requireHints: true,
    requireExplanation: true,
    requireVisuals: false,
    distributionCheck: true
  },
  // FG4 Fase 3 — FULL SYSTEM ENFORCE (11 juli 2026).
  // The staged ramp is complete: every content module is now on 'enforce'.
  // Pedagogy findings are evaluated AND block the gate on failure — full
  // strictness across the whole domain, not just REK. This is a deliberate
  // hard flip authorised because this version is not live and has no active
  // users, so there is no cliff-edge risk of taking a student-facing question
  // offline. Undertagged-but-valid seeds now surface as hard blockers in the
  // pipeline so they get remediated instead of silently accumulating.
  //
  // Ramp history:
  //   - REK first on shadow 2 juni 2026 (GDLA-01).
  //   - Remaining 9 modules added to shadow 10 juni 2026 (telemetry-only).
  //   - REK promoted shadow → warn 21 juni 2026 (FG4 Fase 2).
  //   - V-17 (settings-save dropped enforcement/graceLedger) fixed 11 juli 2026
  //     with read-modify-write in GovernanceSettingsTab.handleSave, so this
  //     baseline can no longer be silently overwritten by an unrelated save.
  //   - ALL 10 modules promoted → 'enforce' 11 juli 2026 (FG4 Fase 3, this).
  // To dial a single module back, move it to 'warn'/'shadow' via the admin
  // Enforcement section — fully reversible and now persistence-safe.
  enforcement: {
    pedagogical: {
      REK: 'enforce',
      GET: 'enforce',
      GELD: 'enforce',
      TIJD: 'enforce',
      MET: 'enforce',
      SPR: 'enforce',
      SWW: 'enforce',
      TAA: 'enforce',
      STU: 'enforce',
      TOPO: 'enforce'
    },
    schemas: {}
  },
  // FG4 Fase 3 — grace ledger emptied for the full-enforce flip (11 juli 2026).
  // A true all-enforce state is incompatible with a live grace entry: any
  // active grace degrades matching blockers to coach-level, which would
  // quietly weaken the promised hard enforcement for that scope. The former
  // REK entry (V-10's first live entry) is therefore retired — its debt is now
  // enforced rather than deferred. The mechanism, resolvers (findActiveGrace),
  // and admin GraceLedgerSection remain fully intact; operators can add a new
  // NAMED, OWNED, EXPIRING entry at any time if a temporary exception is ever
  // needed again.
  graceLedger: [],
  notifications: {
    notifyBlockers: true,
    notifyWarnings: false,
    notifyWeeklyReport: true,
    notifyNewRules: true
  },
  system: {
    cacheEnabled: true,
    debugMode: false,
    autoValidateOnSave: true,
    batchSize: 100
  }
};

// ─── Store Operations ─────────────────────────────────────────────────────────

/**
 * Load governance config from dataLayer.
 * Returns default config if nothing is stored or if stored data is invalid.
 */
export async function loadGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  try {
    const parsed = await dataLayer.getGovernanceConfig();
    if (!parsed) return {
      ...DEFAULT_CONFIG
    };

    // Version check
    if (parsed.version !== 1) {
      warn('Governance config version mismatch, using defaults');
      return {
        ...DEFAULT_CONFIG
      };
    }

    // Merge with defaults to fill any missing fields
    return mergeWithDefaults(parsed as Partial<GovernanceConfigOverrides>);
  } catch (e) {
    warn('Failed to load governance config, using defaults');
    return {
      ...DEFAULT_CONFIG
    };
  }
}

/**
 * Save governance config via dataLayer.
 */
export async function saveGovernanceConfig(config: GovernanceConfigOverrides): Promise<boolean> {
  try {
    const toSave: GovernanceConfigOverrides = {
      ...config,
      version: 1,
      lastSaved: Date.now()
    };
    const success = await dataLayer.saveGovernanceConfig(toSave as Record<string, unknown>);
    if (success) {
      debug('✅ Governance config saved');
    }
    return success !== false;
  } catch (e) {
    warn('Failed to save governance config');
    return false;
  }
}

/**
 * Reset governance config to defaults via dataLayer.
 */
export async function resetGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  await dataLayer.resetGovernanceConfig();
  debug('🔄 Governance config reset to defaults');
  return {
    ...DEFAULT_CONFIG
  };
}

/**
 * Get the default config (for reset operations).
 */
export function getDefaultGovernanceConfig(): GovernanceConfigOverrides {
  return {
    ...DEFAULT_CONFIG
  };
}

/**
 * Get the list of disabled pipeline stages from config.
 * Returns the subset of stage numbers (1-5) that should be skipped.
 *
 * Notes:
 *  - Stage 6 (scoring) and 7 (gate) are not skippable — they always run.
 *  - `pedagogyEnabled` is a sub-toggle of stage 3 and is NOT mapped here;
 *    consumers read it directly and pass it as `PipelineOptions.skipPedagogy`
 *    so the pedagogy sub-stage can be toggled without disabling content.
 *  - `typeEnabled` and `schemaEnabled` both share stage 1; toggling type
 *    alone is therefore a no-op while schema is on (by design — schema and
 *    type form the structural-validation pair).
 */
export function getDisabledStages(config: GovernanceConfigOverrides): number[] {
  const stages = config.pipelineStages;
  if (!stages) return [];
  const disabled: number[] = [];
  if (stages.schemaEnabled === false) disabled.push(STAGES.SCHEMA);
  if (stages.graphEnabled === false) disabled.push(STAGES.GRAPH);
  if (stages.contentEnabled === false) disabled.push(STAGES.CONTENT);
  if (stages.visualEnabled === false) disabled.push(STAGES.VISUAL);
  if (stages.supportEnabled === false) disabled.push(STAGES.SUPPORT);
  // typeEnabled is part of stage 1 (schema+type pair)
  if (stages.typeEnabled === false) disabled.push(STAGES.TYPE);
  return [...new Set(disabled)];
}

// ─── Enforcement & Grace Resolvers (FG4 §F0.C) ──────────────────────────────

/**
 * Resolve the pedagogical enforcement mode for a module. Falls back to
 * `'enforce'` when unconfigured.
 *
 * STRUCTURAL CONTRACT (audit feedback after S4 wiring):
 *   - 'off' MUST be an explicit opt-in — never the silent default. If
 *     `'off'` were the default, supplying `enforcementConfig` to the
 *     orchestrator would silently drop every pedagogy issue (since
 *     applyEnforcementModes treats 'off' as "drop, no telemetry"). That
 *     is exactly the cliff-edge regression FG4 is trying to prevent.
 *   - Therefore: unconfigured scope === "keep current behaviour" === 'enforce'.
 *     Operators move a scope to 'shadow' / 'warn' / 'off' deliberately via
 *     the Settings UI; they never enter those modes accidentally.
 */
export function resolvePedagogicalMode(config: GovernanceConfigOverrides, module: string): EnforcementMode {
  return config.enforcement?.pedagogical?.[module] ?? 'enforce';
}

/**
 * Resolve the strict-schema enforcement mode for a question type. Falls back
 * to `'enforce'` when unconfigured (same contract as resolvePedagogicalMode).
 */
export function resolveSchemaMode(config: GovernanceConfigOverrides, questionType: string): EnforcementMode {
  return config.enforcement?.schemas?.[questionType] ?? 'enforce';
}

/**
 * Find an active (non-expired) grace ledger entry matching the given scope.
 * The most specific match wins: questionId > family > module.
 */
export function findActiveGrace(config: GovernanceConfigOverrides, scope: {
  module?: string;
  family?: string;
  questionId?: string;
}, now: number = Date.now()): GraceLedgerEntry | undefined {
  const ledger = config.graceLedger ?? [];
  const active = ledger.filter(e => {
    const until = Date.parse(e.until);
    // Een onleesbare datum gaf `NaN`, en daarmee sloeg de vervalcheck over —
    // de uitzondering bleef dan VOOR ALTIJD actief. Precies verkeerd om: een
    // grace-periode zonder geldige einddatum is geen grace-periode maar een
    // permanente vrijstelling, en het punt van dit register is juist dat een
    // uitzondering tijdelijk is.
    if (!Number.isFinite(until)) {
      warn(`[Governance] Grace-entry met onleesbare einddatum "${e.until}" wordt ` + 'genegeerd. Een uitzondering zonder geldige vervaldatum zou permanent zijn.');
      return false;
    }
    if (until < now) return false;
    if (e.scope.questionId && e.scope.questionId === scope.questionId) return true;
    if (e.scope.family && e.scope.family === scope.family) return true;
    if (e.scope.module && e.scope.module === scope.module && !e.scope.family && !e.scope.questionId) return true;
    return false;
  });
  // Specificity ranking
  return active.sort((a, b) => specificity(b.scope) - specificity(a.scope))[0];
}
function specificity(scope: GraceLedgerEntry['scope']): number {
  if (scope.questionId) return 3;
  if (scope.family) return 2;
  if (scope.module) return 1;
  return 0;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeWithDefaults(stored: Partial<GovernanceConfigOverrides>): GovernanceConfigOverrides {
  return {
    version: 1,
    qualityGates: {
      ...DEFAULT_CONFIG.qualityGates,
      ...stored.qualityGates
    },
    pipelineStages: {
      ...DEFAULT_CONFIG.pipelineStages,
      ...stored.pipelineStages
    },
    pipelineBehavior: {
      ...DEFAULT_CONFIG.pipelineBehavior,
      ...stored.pipelineBehavior
    },
    policy: {
      ...DEFAULT_CONFIG.policy,
      ...stored.policy
    },
    enforcement: {
      pedagogical: {
        ...DEFAULT_CONFIG.enforcement?.pedagogical,
        ...stored.enforcement?.pedagogical
      },
      schemas: {
        ...DEFAULT_CONFIG.enforcement?.schemas,
        ...stored.enforcement?.schemas
      }
    },
    graceLedger: stored.graceLedger ?? DEFAULT_CONFIG.graceLedger ?? [],
    notifications: {
      ...DEFAULT_CONFIG.notifications,
      ...stored.notifications
    },
    system: {
      ...DEFAULT_CONFIG.system,
      ...stored.system
    },
    lastSaved: stored.lastSaved
  };
}